/**
 * FormulaEditor Component (TASK-215 Phase 3)
 *
 * Visual formula builder with an editable textarea. Buttons insert at cursor
 * position. Users can also type directly. Client-side validation mirrors
 * backend formula_parser.py.
 */
import { useState, useCallback, useRef } from 'react';
import { Eraser, Check, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// ── Types ────────────────────────────────────────────────────────────────────

export interface FormulaVariable {
  name: string;
  label: string;
  unit?: string;
  source: 'input' | 'child' | 'system';
}

export interface FormulaEditorProps {
  value: string;
  onChange: (formula: string) => void;
  variables: FormulaVariable[];
  mode: 'calculator' | 'postprocess';
}

// ── Validation ───────────────────────────────────────────────────────────────

interface ValidationResult {
  valid: boolean;
  error?: string;
}

const TOKEN_RE =
  /(\d+\.?\d*|\.\d+)|([a-zA-Zа-яА-ЯёЁ_][a-zA-Zа-яА-ЯёЁ0-9_]*)|(>=|<=|==|!=|[+\-*/(),><])|(\s+)|(.)/g;

const BUILTINS = new Set(['IF', 'SWITCH']);
const ALLOWED_OPS = new Set(['+', '-', '*', '/', '>', '<', '>=', '<=', '==', '(', ')', ',', '!=']);

type TokenType = 'number' | 'ident' | 'builtin' | 'op' | 'lparen' | 'rparen' | 'comma';

interface Token {
  type: TokenType;
  value: string;
}

function tokenize(formula: string, variableNames: string[]): Token[] | ValidationResult {
  const varSet = new Set(variableNames);
  const re = new RegExp(TOKEN_RE.source, TOKEN_RE.flags);
  const tokens: Token[] = [];
  let match;

  while ((match = re.exec(formula)) !== null) {
    const [, num, ident, op, ws, unknown] = match;
    if (ws) continue;
    if (num) { tokens.push({ type: 'number', value: num }); continue; }
    if (ident) {
      if (BUILTINS.has(ident.toUpperCase())) {
        tokens.push({ type: 'builtin', value: ident.toUpperCase() });
      } else if (varSet.has(ident)) {
        tokens.push({ type: 'ident', value: ident });
      } else {
        return { valid: false, error: `Неизвестная переменная: ${ident}` };
      }
      continue;
    }
    if (op) {
      if (!ALLOWED_OPS.has(op)) {
        return { valid: false, error: `Недопустимый оператор: ${op}` };
      }
      if (op === '(') tokens.push({ type: 'lparen', value: op });
      else if (op === ')') tokens.push({ type: 'rparen', value: op });
      else if (op === ',') tokens.push({ type: 'comma', value: op });
      else tokens.push({ type: 'op', value: op });
      continue;
    }
    if (unknown) {
      return { valid: false, error: `Недопустимый символ: ${unknown}` };
    }
  }

  return tokens;
}

function countFunctionArgs(tokens: Token[], startIndex: number): number {
  let depth = 0;
  let args = 1;
  let hasContent = false;

  for (let i = startIndex; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.type === 'lparen') depth++;
    else if (t.type === 'rparen') {
      depth--;
      if (depth === 0) return hasContent ? args : 0;
    } else if (t.type === 'comma' && depth === 1) {
      args++;
    } else if (depth >= 1) {
      hasContent = true;
    }
  }
  return args;
}

function validateFormula(
  formula: string,
  variableNames: string[]
): ValidationResult {
  if (!formula.trim()) {
    return { valid: false, error: 'Формула пуста' };
  }

  // 1. Parentheses balance
  let depth = 0;
  for (const char of formula) {
    if (char === '(') depth++;
    if (char === ')') depth--;
    if (depth < 0) {
      return { valid: false, error: 'Непарные скобки: лишняя закрывающая скобка' };
    }
  }
  if (depth !== 0) {
    return { valid: false, error: 'Непарные скобки: не хватает закрывающей скобки' };
  }

  // 2. Tokenize
  const result = tokenize(formula, variableNames);
  if (!Array.isArray(result)) return result;
  const tokens = result;

  if (tokens.length === 0) {
    return { valid: false, error: 'Формула пуста' };
  }

  // 3. Adjacent values without operator: "Ширина 4", "2 2"
  const VALUE_TYPES: Set<TokenType> = new Set(['number', 'ident']);
  for (let i = 0; i < tokens.length - 1; i++) {
    const cur = tokens[i];
    const next = tokens[i + 1];
    if (VALUE_TYPES.has(cur.type) && VALUE_TYPES.has(next.type)) {
      return { valid: false, error: `Два значения подряд без оператора: "${cur.value}" и "${next.value}"` };
    }
    // value followed by builtin (e.g. "2 IF") or builtin followed by value without (
    if (VALUE_TYPES.has(cur.type) && next.type === 'builtin') {
      return { valid: false, error: `Пропущен оператор перед ${next.value}` };
    }
  }

  // 4. Function argument count
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].type !== 'builtin') continue;
    const funcName = tokens[i].value;
    if (i + 1 >= tokens.length || tokens[i + 1].type !== 'lparen') {
      return { valid: false, error: `${funcName} должна сопровождаться скобками: ${funcName}(...)` };
    }
    const argCount = countFunctionArgs(tokens, i + 1);
    if (funcName === 'IF') {
      if (argCount === 0) {
        return { valid: false, error: 'IF(): аргументы пусты. Формат: IF(условие, результат_да, результат_нет)' };
      }
      if (argCount !== 3) {
        return { valid: false, error: `IF() требует 3 аргумента (условие, да, нет), получено: ${argCount}` };
      }
    }
    if (funcName === 'SWITCH') {
      if (argCount === 0) {
        return { valid: false, error: 'SWITCH(): аргументы пусты. Формат: SWITCH(ключ, "вариант", цена, ...)' };
      }
      if (argCount < 3) {
        return { valid: false, error: `SWITCH() требует минимум 3 аргумента (ключ + хотя бы одна пара), получено: ${argCount}` };
      }
      if (argCount % 2 === 0) {
        return { valid: false, error: `SWITCH() требует нечётное число аргументов (ключ + пары вариант/цена), получено: ${argCount}` };
      }
    }
  }

  // 5. Trailing/leading operator
  const first = tokens[0];
  if (first.type === 'op' && first.value !== '-') {
    return { valid: false, error: `Формула не может начинаться с оператора "${first.value}"` };
  }
  const last = tokens[tokens.length - 1];
  if (last.type === 'op' || last.type === 'comma') {
    return { valid: false, error: `Формула не может заканчиваться на "${last.value}"` };
  }

  return { valid: true };
}

// ── Operator definitions ─────────────────────────────────────────────────────

interface OperatorDef {
  label: string;
  value: string;
  testId: string;
}

const OPERATORS: OperatorDef[] = [
  { label: '+', value: ' + ', testId: 'operator-plus' },
  { label: '-', value: ' - ', testId: 'operator-minus' },
  { label: '*', value: ' * ', testId: 'operator-multiply' },
  { label: '/', value: ' / ', testId: 'operator-divide' },
  { label: '(', value: '(', testId: 'operator-lparen' },
  { label: ')', value: ')', testId: 'operator-rparen' },
];

// ── Variable source color mapping ────────────────────────────────────────────

const SOURCE_STYLES: Record<FormulaVariable['source'], string> = {
  input: 'border-blue-300 text-blue-700 hover:bg-blue-50',
  child: 'border-green-300 text-green-700 hover:bg-green-50',
  system: 'border-purple-300 text-purple-700 hover:bg-purple-50',
};

// ── Component ────────────────────────────────────────────────────────────────

const MODE_HINTS: Record<FormulaEditorProps['mode'], { description: string; placeholder: string }> = {
  calculator: {
    description: 'Формула вычисляет результат из входных параметров и констант. Используйте кнопки ниже или пишите вручную.',
    placeholder: 'Пример: длина * ширина * цена_бетона',
  },
  postprocess: {
    description: 'Пост-формула применяется к базовому результату. Например, надбавка 15% или условная скидка.',
    placeholder: 'Пример: базовая_сумма * 1.15',
  },
};

export function FormulaEditor({
  value,
  onChange,
  variables,
  mode,
}: FormulaEditorProps): JSX.Element {
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hints = MODE_HINTS[mode];

  // ── Insert at cursor position ──────────────────────────────────────────────

  const insertAtCursor = useCallback(
    (text: string, cursorOffset?: number) => {
      setValidation(null);
      const ta = textareaRef.current;
      if (!ta) {
        onChange(value + text);
        return;
      }

      const start = ta.selectionStart ?? value.length;
      const end = ta.selectionEnd ?? value.length;
      const newValue = value.slice(0, start) + text + value.slice(end);
      onChange(newValue);

      const newPos = cursorOffset !== undefined
        ? start + cursorOffset
        : start + text.length;

      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      });
    },
    [value, onChange]
  );

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleVariableClick = useCallback(
    (varName: string) => {
      insertAtCursor(varName);
    },
    [insertAtCursor]
  );

  const handleOperatorClick = useCallback(
    (opValue: string) => {
      insertAtCursor(opValue);
    },
    [insertAtCursor]
  );

  const handleInsertFunction = useCallback(
    (funcName: string) => {
      const template = `${funcName}(, , )`;
      // Place cursor inside first argument: after "IF("
      insertAtCursor(template, funcName.length + 1);
    },
    [insertAtCursor]
  );

  const handleClear = useCallback(() => {
    setValidation(null);
    onChange('');
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [onChange]);

  const handleValidate = useCallback(() => {
    const variableNames = variables.map((v) => v.name);
    const result = validateFormula(value, variableNames);
    setValidation(result);
  }, [value, variables]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium leading-none">
          Формула
        </label>
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Справка по формулам"
        >
          <Info className="h-4 w-4" />
        </button>
      </div>

      <p className="text-xs text-gray-500 -mt-1">{hints.description}</p>

      {/* Collapsible help */}
      {showHelp && (
        <div className="text-xs bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2">
          <p className="font-medium text-blue-800">Как строить формулы</p>
          <ul className="space-y-1 text-blue-700">
            <li><span className="font-mono bg-blue-100 px-1 rounded">длина * ширина * цена</span> — арифметика с переменными</li>
            <li><span className="font-mono bg-blue-100 px-1 rounded">IF(условие, да, нет)</span> — если условие истинно → первый результат, иначе → второй</li>
            <li className="pl-4 text-blue-600">Пример: <span className="font-mono">IF(площадь {'>'} 100, цена * 0.9, цена)</span> — скидка 10% при площади больше 100</li>
            <li><span className="font-mono bg-blue-100 px-1 rounded">SWITCH(ключ, "вар1", цена1, ...)</span> — выбор значения по варианту</li>
            <li className="pl-4 text-blue-600">Пример: <span className="font-mono">SWITCH(класс, "эконом", 1000, "стандарт", 2000)</span></li>
            <li>IF можно совмещать с арифметикой: <span className="font-mono bg-blue-100 px-1 rounded">длина * IF(тип == 1, 500, 800)</span></li>
          </ul>
        </div>
      )}

      {/* Editable formula textarea */}
      <div data-testid="formula-preview" className="relative">
        <textarea
          ref={textareaRef}
          data-testid="formula-textarea"
          value={value}
          onChange={(e) => {
            setValidation(null);
            onChange(e.target.value);
          }}
          placeholder={hints.placeholder}
          className="w-full bg-gray-50 border rounded-md p-3 font-mono text-sm min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
          rows={2}
        />
      </div>

      {/* Variables */}
      {variables.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Переменные
          </span>
          <div
            data-testid="variables-dropdown"
            className="flex flex-wrap gap-1.5"
          >
            {variables.map((v) => (
              <Button
                key={v.name}
                data-testid={`variable-${v.name}`}
                variant="outline"
                size="sm"
                type="button"
                className={cn('h-7 px-2 text-xs', SOURCE_STYLES[v.source])}
                onClick={() => handleVariableClick(v.name)}
              >
                {v.label}
                {v.unit && (
                  <span className="ml-1 opacity-60">({v.unit})</span>
                )}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Operators */}
      <div className="space-y-1.5">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Операторы
        </span>
        <div className="flex flex-wrap gap-1.5">
          {OPERATORS.map((op) => (
            <Button
              key={op.testId}
              data-testid={op.testId}
              variant="outline"
              size="sm"
              type="button"
              className="h-7 w-7 p-0 font-mono text-sm"
              onClick={() => handleOperatorClick(op.value)}
            >
              {op.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Functions */}
      <div className="space-y-1.5">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Функции
        </span>
        <div className="space-y-1.5">
          <button
            data-testid="function-if"
            type="button"
            onClick={() => handleInsertFunction('IF')}
            className="w-full text-left border rounded-md px-3 py-2 hover:bg-gray-50 transition-colors group"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-xs font-semibold text-blue-600 group-hover:text-blue-700">IF</span>
              <span className="font-mono text-xs text-gray-600">( условие, результат_да, результат_нет )</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Пример: IF(площадь {'>'} 100, цена * 0.9, цена)
            </p>
          </button>
          <button
            data-testid="function-switch"
            type="button"
            onClick={() => handleInsertFunction('SWITCH')}
            className="w-full text-left border rounded-md px-3 py-2 hover:bg-gray-50 transition-colors group"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-xs font-semibold text-blue-600 group-hover:text-blue-700">SWITCH</span>
              <span className="font-mono text-xs text-gray-600">( ключ, "вариант1", цена1, "вариант2", цена2, ... )</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Пример: SWITCH(класс, "эконом", 1000, "стандарт", 2000)
            </p>
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1.5 pt-1">
        <Button
          data-testid="clear-button"
          variant="outline"
          size="sm"
          type="button"
          className="h-7 px-2 text-xs"
          onClick={handleClear}
        >
          <Eraser className="h-3.5 w-3.5 mr-1" />
          Очистить
        </Button>
        <div className="flex-1" />
        <Button
          data-testid="validate-button"
          variant="outline"
          size="sm"
          type="button"
          className="h-7 px-2 text-xs"
          onClick={handleValidate}
        >
          <Check className="h-3.5 w-3.5 mr-1" />
          Проверить
        </Button>
      </div>

      {/* Validation result */}
      {validation && (
        <div
          data-testid="validation-result"
          className={cn(
            'text-sm px-2 py-1 rounded',
            validation.valid
              ? 'text-green-600 bg-green-50'
              : 'text-red-500 bg-red-50'
          )}
        >
          {validation.valid ? 'Формула корректна' : validation.error}
        </div>
      )}
    </div>
  );
}
